import MultiSelectPillList from '../../../../components/MultiSelectPillList'
import SingleInputSelect from '../../../../components/SingleInputSelect'
import UserMultiselect from '../../../../components/UserMultiselect'
/*
 * Configurations of the policies' inputs
 * NOTE: Right now we are considering that all inputs are arrays of strings.
 *       If the type of the input being introduced is different make sure to adapt code accordingly.
 *
 * For template and policy:
 * - Add new input to the constants in this file
 *
 * For templating:
 * - Filter tenant properties in filterTenantProperties
 *
 * For creating/editing a policy:
 * - Adapt values in toFormValues and buildPolicyPayload if necessary
 * - Add options to getPolicyFormOptions
 * - Add to FormattedPolicyFrame and PolicyFormInputs (or to GrantControls)
 * - Validate input in POLICY_FORM_VALIDATIONS
 * - Test input in policy_management/utils.test.js
 */

export const CONDITIONS_INPUTS = {
  includeUsers: 'includeUsers',
  excludeUsers: 'excludeUsers',
  includeGroups: 'includeGroups',
  excludeGroups: 'excludeGroups',
  includeRoles: 'includeRoles',
  excludeRoles: 'excludeRoles',
  includeLocations: 'includeLocations',
  excludeLocations: 'excludeLocations',
  includeApplications: 'includeApplications',
  excludeApplications: 'excludeApplications',
  clientAppTypes: 'clientAppTypes',
  includePlatforms: 'includePlatforms',
  excludePlatforms: 'excludePlatforms',
  userRiskLevels: 'userRiskLevels',
  signInRiskLevels: 'signInRiskLevels',
  insiderRiskLevels: 'insiderRiskLevels',
  transferMethods: 'transferMethods',
  includeGuestsOrExternalUsers: 'includeGuestsOrExternalUsers',
  excludeGuestsOrExternalUsers: 'excludeGuestsOrExternalUsers',
  includeUserActions: 'includeUserActions',
}

export const GRANT_CONTROLS_INPUTS = {
  builtInControls: 'builtInControls',
  grantControlsOperator: 'grantControlsOperator',
  customAuthenticationFactors: 'customAuthenticationFactors',
  authenticationStrength: 'authenticationStrength',
}

export const GENERAL_INPUTS = {
  policyStatus: 'policyStatus',
}

/* Input keys (name of the inputs in the policy object) */
export const INPUT_KEYS = {
  ...CONDITIONS_INPUTS,
  ...GRANT_CONTROLS_INPUTS,
  ...GENERAL_INPUTS,
}

export const INPUT_LIST = Object.values(INPUT_KEYS)
export const CONDITIONS_INPUTS_LIST = Object.values(CONDITIONS_INPUTS)
export const GRANT_CONTROLS_INPUTS_LIST = Object.values(GRANT_CONTROLS_INPUTS)
export const GENERAL_INPUTS_LIST = Object.values(GENERAL_INPUTS)

export const isGroupInput = (inputName) => [INPUT_KEYS.includeGroups, INPUT_KEYS.excludeGroups].includes(inputName)

export const isNamedLocationInput = (inputName) =>
  [INPUT_KEYS.includeLocations, INPUT_KEYS.excludeLocations].includes(inputName)

/* Options for the policy template */
export const INPUT_OPTIONS = [
  { label: 'Include Users', value: INPUT_KEYS.includeUsers },
  { label: 'Exclude Users', value: INPUT_KEYS.excludeUsers },
  { label: 'Include Groups', value: INPUT_KEYS.includeGroups },
  { label: 'Exclude Groups', value: INPUT_KEYS.excludeGroups },
  { label: 'Include Roles', value: INPUT_KEYS.includeRoles },
  { label: 'Exclude Roles', value: INPUT_KEYS.excludeRoles },
  { label: 'Include Guest or External Users', value: INPUT_KEYS.includeGuestsOrExternalUsers },
  { label: 'Exclude Guest or External Users', value: INPUT_KEYS.excludeGuestsOrExternalUsers },
  { label: 'Include Locations', value: INPUT_KEYS.includeLocations },
  { label: 'Exclude Locations', value: INPUT_KEYS.excludeLocations },
  { label: 'Include Applications', value: INPUT_KEYS.includeApplications },
  { label: 'Exclude Applications', value: INPUT_KEYS.excludeApplications },
  { label: 'User Actions', value: INPUT_KEYS.includeUserActions },
  { label: 'Client Apps', value: INPUT_KEYS.clientAppTypes },
  { label: 'Include Device Platforms', value: INPUT_KEYS.includePlatforms },
  { label: 'Exclude Device Platforms', value: INPUT_KEYS.excludePlatforms },
  { label: 'User Risk', value: INPUT_KEYS.userRiskLevels },
  { label: 'Sign-in Risk', value: INPUT_KEYS.signInRiskLevels },
  { label: 'Insider Risk', value: INPUT_KEYS.insiderRiskLevels },
  { label: 'Transfer methods', value: INPUT_KEYS.transferMethods },
]

export const INPUT_OPTIONS_MAP = {}
INPUT_OPTIONS.forEach((option) => {
  INPUT_OPTIONS_MAP[option.value] = option.label
})

/* path of the inputs in the policy json */
export const INPUT_POLICY_PATH = {
  // conditions inputs
  [INPUT_KEYS.includeUsers]: 'conditions.users.includeUsers',
  [INPUT_KEYS.excludeUsers]: 'conditions.users.excludeUsers',
  [INPUT_KEYS.includeGroups]: 'conditions.users.includeGroups',
  [INPUT_KEYS.excludeGroups]: 'conditions.users.excludeGroups',
  [INPUT_KEYS.includeRoles]: 'conditions.users.includeRoles',
  [INPUT_KEYS.excludeRoles]: 'conditions.users.excludeRoles',
  [INPUT_KEYS.includeLocations]: 'conditions.locations.includeLocations',
  [INPUT_KEYS.excludeLocations]: 'conditions.locations.excludeLocations',
  [INPUT_KEYS.includeApplications]: 'conditions.applications.includeApplications',
  [INPUT_KEYS.excludeApplications]: 'conditions.applications.excludeApplications',
  [INPUT_KEYS.clientAppTypes]: 'conditions.clientAppTypes',
  [INPUT_KEYS.includePlatforms]: 'conditions.platforms.includePlatforms',
  [INPUT_KEYS.excludePlatforms]: 'conditions.platforms.excludePlatforms',
  [INPUT_KEYS.userRiskLevels]: 'conditions.userRiskLevels',
  [INPUT_KEYS.signInRiskLevels]: 'conditions.signInRiskLevels',
  [INPUT_KEYS.insiderRiskLevels]: 'conditions.insiderRiskLevels',
  [INPUT_KEYS.transferMethods]: 'conditions.authenticationFlows.transferMethods',
  [INPUT_KEYS.includeGuestsOrExternalUsers]: 'conditions.users.includeGuestsOrExternalUsers.guestOrExternalUserTypes',
  [INPUT_KEYS.excludeGuestsOrExternalUsers]: 'conditions.users.excludeGuestsOrExternalUsers.guestOrExternalUserTypes',
  [INPUT_KEYS.includeUserActions]: 'conditions.applications.includeUserActions',

  // grant controls
  [INPUT_KEYS.builtInControls]: 'grantControls.builtInControls',
  [INPUT_KEYS.grantControlsOperator]: 'grantControls.operator',
  [INPUT_KEYS.customAuthenticationFactors]: 'grantControls.customAuthenticationFactors',
  [INPUT_KEYS.authenticationStrength]: 'grantControls.authenticationStrength.id',

  // general
  [INPUT_KEYS.policyStatus]: 'state',
}

export const INPUT_TITLE_MAP = {
  [INPUT_KEYS.includeUsers]: 'POLICY_TEMPLATES.INPUTS.INCLUDE_USERS',
  [INPUT_KEYS.excludeUsers]: 'POLICY_TEMPLATES.INPUTS.EXCLUDE_USERS',
  [INPUT_KEYS.includeGroups]: 'POLICY_TEMPLATES.INPUTS.INCLUDE_GROUPS',
  [INPUT_KEYS.excludeGroups]: 'POLICY_TEMPLATES.INPUTS.EXCLUDE_GROUPS',
  [INPUT_KEYS.includeRoles]: 'POLICY_TEMPLATES.INPUTS.INCLUDE_ROLES',
  [INPUT_KEYS.excludeRoles]: 'POLICY_TEMPLATES.INPUTS.EXCLUDE_ROLES',
  [INPUT_KEYS.includeLocations]: 'POLICY_TEMPLATES.INPUTS.INCLUDE_LOCATIONS',
  [INPUT_KEYS.excludeLocations]: 'POLICY_TEMPLATES.INPUTS.EXCLUDE_LOCATIONS',
  [INPUT_KEYS.includeApplications]: 'POLICY_TEMPLATES.INPUTS.INCLUDE_APPLICATIONS',
  [INPUT_KEYS.excludeApplications]: 'POLICY_TEMPLATES.INPUTS.EXCLUDE_APPLICATIONS',
  [INPUT_KEYS.clientAppTypes]: 'POLICY_TEMPLATES.INPUTS.CLIENT_APPS',
  [INPUT_KEYS.includePlatforms]: 'POLICY_TEMPLATES.INPUTS.INCLUDE_PLATFORMS',
  [INPUT_KEYS.excludePlatforms]: 'POLICY_TEMPLATES.INPUTS.EXCLUDE_PLATFORMS',
  [INPUT_KEYS.userRiskLevels]: 'POLICY_TEMPLATES.INPUTS.USER_RISK',
  [INPUT_KEYS.signInRiskLevels]: 'POLICY_TEMPLATES.INPUTS.SIGNIN_RISK',
  [INPUT_KEYS.insiderRiskLevels]: 'POLICY_TEMPLATES.INPUTS.INSIDER_RISK',
  [INPUT_KEYS.transferMethods]: 'POLICY_TEMPLATES.INPUTS.TRANSFER_METHODS',
  [INPUT_KEYS.includeGuestsOrExternalUsers]: 'POLICY_TEMPLATES.INPUTS.INCLUDE_GUEST_USERS',
  [INPUT_KEYS.excludeGuestsOrExternalUsers]: 'POLICY_TEMPLATES.INPUTS.EXCLUDE_GUEST_USERS',
  [INPUT_KEYS.includeUserActions]: 'POLICY_TEMPLATES.INPUTS.USER_ACTIONS',

  // grant controls
  [INPUT_KEYS.builtInControls]: 'POLICY_TEMPLATES.BUILT_IN_CONTROLS',
  [INPUT_KEYS.authenticationStrength]: 'POLICY_TEMPLATES.REQUIRE_AUTHENTICATION_STRENGTH',
  [INPUT_KEYS.customAuthenticationFactors]: 'POLICY_TEMPLATES.CUSTOM_AUTH_FACTORS',
  [INPUT_KEYS.grantControlsOperator]: 'POLICY_TEMPLATES.FOR_MULTIPLE_CONTROLS',

  // general
  [INPUT_KEYS.policyStatus]: 'GENERAL.STATUS',
}

export const INPUT_FORM_COMPONENT = {
  [INPUT_KEYS.includeUsers]: UserMultiselect,
  [INPUT_KEYS.excludeUsers]: UserMultiselect,
  [INPUT_KEYS.includeGroups]: MultiSelectPillList,
  [INPUT_KEYS.excludeGroups]: MultiSelectPillList,
  [INPUT_KEYS.includeRoles]: MultiSelectPillList,
  [INPUT_KEYS.excludeRoles]: MultiSelectPillList,
  [INPUT_KEYS.includeLocations]: MultiSelectPillList,
  [INPUT_KEYS.excludeLocations]: MultiSelectPillList,
  [INPUT_KEYS.includeApplications]: MultiSelectPillList,
  [INPUT_KEYS.excludeApplications]: MultiSelectPillList,
  [INPUT_KEYS.clientAppTypes]: MultiSelectPillList,
  [INPUT_KEYS.includePlatforms]: MultiSelectPillList,
  [INPUT_KEYS.excludePlatforms]: MultiSelectPillList,
  [INPUT_KEYS.userRiskLevels]: MultiSelectPillList,
  [INPUT_KEYS.signInRiskLevels]: MultiSelectPillList,
  [INPUT_KEYS.insiderRiskLevels]: MultiSelectPillList,
  [INPUT_KEYS.transferMethods]: MultiSelectPillList,
  [INPUT_KEYS.includeGuestsOrExternalUsers]: MultiSelectPillList,
  [INPUT_KEYS.excludeGuestsOrExternalUsers]: MultiSelectPillList,
  [INPUT_KEYS.includeUserActions]: SingleInputSelect,

  // grant controls
  [INPUT_KEYS.builtInControls]: MultiSelectPillList,
  [INPUT_KEYS.authenticationStrength]: SingleInputSelect,
  [INPUT_KEYS.customAuthenticationFactors]: MultiSelectPillList,
  [INPUT_KEYS.grantControlsOperator]: SingleInputSelect,

  // general
  [INPUT_KEYS.policyStatus]: SingleInputSelect,
}

export const INPUT_TO_EMPTY_VALUE = {
  [INPUT_KEYS.includeUsers]: [],
  [INPUT_KEYS.excludeUsers]: [],
  [INPUT_KEYS.includeGroups]: [],
  [INPUT_KEYS.excludeGroups]: [],
  [INPUT_KEYS.includeRoles]: [],
  [INPUT_KEYS.excludeRoles]: [],
  [INPUT_KEYS.includeLocations]: [],
  [INPUT_KEYS.excludeLocations]: [],
  [INPUT_KEYS.includeApplications]: [],
  [INPUT_KEYS.excludeApplications]: [],
  [INPUT_KEYS.clientAppTypes]: [],
  [INPUT_KEYS.includePlatforms]: [],
  [INPUT_KEYS.excludePlatforms]: [],
  [INPUT_KEYS.userRiskLevels]: [],
  [INPUT_KEYS.signInRiskLevels]: [],
  [INPUT_KEYS.insiderRiskLevels]: [],
  [INPUT_KEYS.transferMethods]: [],
  [INPUT_KEYS.includeGuestsOrExternalUsers]: [],
  [INPUT_KEYS.excludeGuestsOrExternalUsers]: [],
  [INPUT_KEYS.includeUserActions]: null,

  // grant controls
  [INPUT_KEYS.builtInControls]: [],
  [INPUT_KEYS.authenticationStrength]: null,
  [INPUT_KEYS.customAuthenticationFactors]: [],
  [INPUT_KEYS.grantControlsOperator]: null,

  // general
  [INPUT_KEYS.policyStatus]: null,
}
